import {
  StringConditionVariable,
  ReferenceConditionVariable,
  PrimitiveConditionVariable,
  ConditionVariableDiscreteOption,
  ConditionVariable,
  isStringConditionVariable,
} from './types'

/**
 * Creates a StringConditionVariable with standard defaults
 */
export const createStringConditionVariable = (params: {
  id: string
  lhs: StringConditionVariable['lhs']
  discreteOptions?: StringConditionVariable['discreteOptions']
  operatorOverrides?: StringConditionVariable['operatorOverrides']
  resolvedType?: StringConditionVariable['resolvedType']
  invalidOperators?: StringConditionVariable['invalidOperators']
}): StringConditionVariable => {
  return {
    id: params.id,
    lhs: params.lhs,
    resolvedType: params.resolvedType ?? 'string',
    discreteOptions: params.discreteOptions ?? null,
    operatorOverrides: params.operatorOverrides ?? null,
    invalidOperators: params.invalidOperators ?? [],
  }
}

/**
 * Creates a ReferenceConditionVariable with standard defaults
 */
export const createReferenceConditionVariable = (params: {
  id: string
  lhs: ReferenceConditionVariable['lhs']
  reference: ReferenceConditionVariable['reference']
  resolvedType: ReferenceConditionVariable['resolvedType']
  invalidOperators?: ReferenceConditionVariable['invalidOperators']
  additionalOptions?: ReferenceConditionVariable['additionalOptions']
}): ReferenceConditionVariable => {
  return {
    id: params.id,
    lhs: params.lhs,
    resolvedType: params.resolvedType,
    reference: params.reference,
    invalidOperators: params.invalidOperators ?? [],
    operatorOverrides: null,
    additionalOptions: params.additionalOptions ?? [],
  }
}

/**
 * Creates a PrimitiveConditionVariable (boolean, number) with standard defaults
 */
export const createPrimitiveConditionVariable = (params: {
  id: string
  lhs: PrimitiveConditionVariable['lhs']
  resolvedType: PrimitiveConditionVariable['resolvedType']
  invalidOperators?: PrimitiveConditionVariable['invalidOperators']
}): PrimitiveConditionVariable => {
  return {
    id: params.id,
    lhs: params.lhs,
    resolvedType: params.resolvedType,
    operatorOverrides: null,
    invalidOperators: params.invalidOperators ?? [],
  }
}

/**
 * Creates a discrete option with value and label
 */
export const createConditionDiscreteOption = (value: string): ConditionVariableDiscreteOption => {
  return {
    rhs: {
      type: 'VARIABLE',
      value,
    },
  }
}

/**
 * Choice values compare by option id, and an id belongs to one choice list (a core data type or a single field's
 * choices), so two choice fields only match when their options overlap
 */
export const choicesCompatible = (left: ConditionVariable, right: ConditionVariable) => {
  const leftOptions = getChoiceOptions(left)
  const rightOptions = getChoiceOptions(right)
  if (leftOptions == null || rightOptions == null) return leftOptions == null && rightOptions == null
  const leftIds = new Set(leftOptions.map((option) => option.rhs.value))
  return rightOptions.some((option) => leftIds.has(option.rhs.value))
}

const getChoiceOptions = (variable: ConditionVariable) => (isStringConditionVariable(variable) ? variable.discreteOptions : null)
